import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Filter,
  Heart,
  LogOut,
  MapPin,
  Menu,
  Search,
  Ticket,
  X
} from 'lucide-react';
import OrganizerCard from './OrganizerCard';
import CartView from './CartView';
import CountryGate from './CountryGate';
import InfoView, { InfoPageKey } from './InfoView';
import EventPreviewModal from './EventPreviewModal';
import { countries, Country } from '../data/countries';
import { ClassData, getClassById, getClasses, getFullImageUrl } from '../services/api';
import { getCurrentUser, getCurrentToken, clearUserSession } from '../utils/userStorage';
import { CartItem, useCart } from '../contexts/CartContext';
import { EventTicketTier } from './EventPreviewModal';
import { getFullName, getNameInitials } from '../utils/userInitials';
import { useScrollToTop } from '../hooks/useScrollToTop';
import { mergePersistedEventLike } from '../utils/eventLikeStorage';
import { buildPeekPassEventFeed, sortPeekPassEvents } from '../utils/eventFeed';
import {
  applyCheckoutUpdatesToCart,
  CheckoutAuthRequiredError,
  CheckoutRevalidationError,
  createMercadoPagoPreference,
  redirectToCheckoutAuth,
  summarizeCheckoutChanges
} from '../utils/checkoutFlow';
import {
  getPeekPassDate,
  getPeekPassEventDate,
  getPeekPassOrganizer,
  getPeekPassSearchText,
  getPeekPassSummary,
  getPeekPassTitle,
  getPeekPassVenue,
  mapCategoryToEvent,
  mapDifficultyToExperience,
  PEEKPASS_BRAND,
  PEEKPASS_DOMAIN
} from '../utils/peekPassBrand';

interface HomeProps {
  onLogin: () => void;
  onRegister: () => void;
  userEmail?: string;
  onViewPackDetails?: (packId: string) => void;
  onGoToDashboard?: () => void;
}

type PaginationItem = number | 'start-gap' | 'end-gap';
const MARKET_COOKIE = 'peekpass-market';
const MARKET_STORAGE_KEY = 'peekpass-market';
const MARKET_MAX_AGE = 60 * 60 * 24 * 180;
const HERO_PUBLIC_IMAGES = [
  '/peekpassasd.png',
  '/peekpassclubfingers.png',
  '/peekpassclubsticker.png',
  '/peekpasseinstein.png',
  '/peekpasschicafiesta.jpg',
  '/peekpasseventoscomida.jpg',
  '/peekpassbar.jpg',
  '/peekpassheart.jpg',
  '/peekpasshiphop.jpg',
  '/peekpasshiphop2.jpg',
  '/peekpassrap.jpg',
  '/peekpassfuegorojo.jpg',
  '/peekpasscalaca.jpg',
  '/peekpassticket_.png',
  '/peekpassteatrocolon.jpg',
  '/teatrocolon.jpg',
  '/peekapsskonex.jpg',
  '/PKSMILE_Logo.png',
  '/vip_mano_peekpass.club.jpg',
  '/peekpassclubverde_headerlogo.png'
];

const readCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const getPersistedMarket = (): Country | null => {
  const selectedCode = window.localStorage.getItem(MARKET_STORAGE_KEY) || readCookie(MARKET_COOKIE);
  return countries.find((country) => country.code === selectedCode) || null;
};

const getPaginationItems = (currentPage: number, totalPages: number): PaginationItem[] => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, 'end-gap', totalPages];
  }

  if (currentPage >= totalPages - 3) {
    return [1, 'start-gap', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, 'start-gap', currentPage - 1, currentPage, currentPage + 1, 'end-gap', totalPages];
};

const Home: React.FC<HomeProps> = ({ onLogin, onRegister, userEmail, onViewPackDetails, onGoToDashboard }) => {
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [infoPage, setInfoPage] = useState<InfoPageKey | null>(null);
  const [showCartView, setShowCartView] = useState(false);
  const [selectedPreviewEvent, setSelectedPreviewEvent] = useState<ClassData | null>(null);
  const [allEvents, setAllEvents] = useState<ClassData[]>([]);
  const [activeHeroImage, setActiveHeroImage] = useState(0);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [market, setMarket] = useState<Country | null>(() => getPersistedMarket());
  const carouselRef = useRef<HTMLDivElement>(null);
  const sharedEventHandledRef = useRef(false);
  const { state: cartState, addToCart, replaceCartItems } = useCart();

  useScrollToTop();

  useEffect(() => {
    const checkUserSession = () => {
      try {
        const user = getCurrentUser();
        const token = getCurrentToken();
        setCurrentUser(user && token ? user : null);
      } catch (sessionError) {
        console.error('Error verificando sesión:', sessionError);
        setCurrentUser(null);
      }
    };

    checkUserSession();
    window.addEventListener('storage', checkUserSession);
    return () => window.removeEventListener('storage', checkUserSession);
  }, []);

  const tryOpenSharedEvent = (events: ClassData[]) => {
    if (sharedEventHandledRef.current) return;
    const sharedId = new URLSearchParams(window.location.search).get('event');
    if (!sharedId) return;

    sharedEventHandledRef.current = true;
    const clearSharedParam = () => {
      window.history.replaceState({}, document.title, window.location.pathname);
    };

    const openEvent = (event: ClassData) => {
      setSelectedPreviewEvent(event);
      clearSharedParam();
    };

    const found = events.find((event) => String(event._id || '') === String(sharedId));
    if (found) {
      openEvent(found);
      return;
    }

    getClassById(sharedId)
      .then((event) => openEvent(buildPeekPassEventFeed([event])[0] || event))
      .catch((error) => {
        console.error('Evento compartido no encontrado:', error);
        clearSharedParam();
      });
  };

  useEffect(() => {
    let active = true;

    const loadEvents = async () => {
      setIsLoadingEvents(true);
      setEventsError(null);

      try {
        const response = await getClasses({ limit: 200, page: 1, sortBy: 'createdAt' });
        if (!active) return;
        const apiEvents = Array.isArray(response.data) ? response.data : [];
        console.log("API EVENTS", apiEvents.length, apiEvents.map(e => ({ id: e._id, title: e.title })));

        console.log("API EVENTS", apiEvents.length, apiEvents.map(e => ({
          id: e._id,
          title: e.title
        })));

        const user = getCurrentUser();
        const userId = String(user?.id || '');
        const feed = buildPeekPassEventFeed(apiEvents);

        console.log("FEED EVENTS", feed.length, feed.map(e => ({
          id: e._id,
          title: e.title
        })));

        const resolvedEvents = feed.map((event) => ({
          ...event,
          likes: mergePersistedEventLike(event.likes, String(event._id || event.title), userId)
        }));
        setAllEvents(resolvedEvents);
        tryOpenSharedEvent(resolvedEvents);
      } catch (error: any) {
        if (!active) return;
        setAllEvents(buildPeekPassEventFeed([]));
        setEventsError(`${error.message || 'No pudimos cargar los eventos.'}`);
        tryOpenSharedEvent([]);
      } finally {
        if (active) setIsLoadingEvents(false);
      }
    };

    loadEvents();
    return () => { active = false; };
  }, []);

  const sortedEvents = useMemo(() => sortPeekPassEvents(allEvents), [allEvents]);
  const monthOptions = useMemo(() => ['all', ...Array.from(new Set(allEvents.map((pack) => (
    new Intl.DateTimeFormat('es-AR', { month: 'long' }).format(getPeekPassEventDate(pack))
  ))))], [allEvents]);
  const categoryOptions = useMemo(() => (
    Array.from(new Set(allEvents.map((pack) => mapCategoryToEvent(pack.category)))).sort((left, right) => left.localeCompare(right, 'es-AR'))
  ), [allEvents]);
  const carouselEvents = useMemo(() => sortedEvents, [sortedEvents]);
  const heroImages = useMemo(() => {
    const uniqueEvents = sortedEvents.filter((event, index, events) => {
      const image = getFullImageUrl(event.thumbnail);
      return Boolean(image) && events.findIndex((candidate) => getFullImageUrl(candidate.thumbnail) === image) === index;
    });
    const priorityTerms = ['fiesta', 'concierto', 'festival', 'teatro', 'hip hop', 'rap', 'experiencia'];
    const prioritized: ClassData[] = [];

    priorityTerms.forEach((term) => {
      const match = uniqueEvents.find((event) => {
        const searchable = `${event.title || ''} ${event.category || ''}`.toLocaleLowerCase('es-AR');
        return searchable.includes(term) && !prioritized.includes(event);
      });
      if (match) prioritized.push(match);
    });

    const eventImages = [...prioritized, ...uniqueEvents.filter((event) => !prioritized.includes(event))]
      .slice(0, 20)
      .map((event) => getFullImageUrl(event.thumbnail));

    return Array.from(new Set([...eventImages, ...HERO_PUBLIC_IMAGES].filter(Boolean)));
  }, [sortedEvents]);

  useEffect(() => {
    setActiveHeroImage(0);
    if (heroImages.length <= 1) return undefined;

    const rotation = window.setInterval(() => {
      setActiveHeroImage((current) => (current + 1) % heroImages.length);
    }, 5000);

    return () => window.clearInterval(rotation);
  }, [heroImages]);

  const filteredPacks = useMemo(() => {
    const filtered = sortedEvents.filter((pack) => {
      const matchesDifficulty = selectedDifficulty === 'all' || pack.difficulty === selectedDifficulty;
      const eventMonth = new Intl.DateTimeFormat('es-AR', { month: 'long' }).format(getPeekPassEventDate(pack));
      const matchesMonth = selectedMonth === 'all' || eventMonth === selectedMonth;
      const matchesCategory = selectedCategory === 'all' || mapCategoryToEvent(pack.category) === selectedCategory;
      const matchesSearch = getPeekPassSearchText(pack).toLocaleLowerCase('es-AR').includes(searchTerm.trim().toLocaleLowerCase('es-AR'));
      return matchesDifficulty && matchesMonth && matchesCategory && matchesSearch;
    });

    return filtered;
  }, [searchTerm, selectedCategory, selectedDifficulty, selectedMonth, sortedEvents]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, selectedDifficulty, selectedMonth]);

  useEffect(() => {
    let animationFrame = 0;
    let lastFrame = performance.now();
    let isPositioned = false;

    // La ficha abierta es la única pausa intencional: al cerrarla el carrusel
    // retoma exactamente desde la tarjeta que estaba mostrando.
    if (selectedPreviewEvent) {
      return undefined;
    }

    if (
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      !window.matchMedia('(min-width: 769px)').matches
    ) {
      return undefined;
    }

    const moveCarousel = (now: number) => {
      const carousel = carouselRef.current;
      const elapsed = Math.min(now - lastFrame, 32);
      lastFrame = now;

      if (carousel && carousel.scrollWidth > carousel.clientWidth) {
        const loopPoint = carousel.scrollWidth / 2;
        if (!isPositioned) {
          if (carousel.scrollLeft >= loopPoint) carousel.scrollLeft = 0;
          isPositioned = true;
        }

        const nextScroll = carousel.scrollLeft + 0.78 * (elapsed / 16);

        carousel.scrollLeft = nextScroll >= loopPoint ? nextScroll - loopPoint : nextScroll;
      }

      animationFrame = window.requestAnimationFrame(moveCarousel);
    };

    animationFrame = window.requestAnimationFrame(moveCarousel);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [carouselEvents, selectedPreviewEvent]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const originalOverflow = document.body.style.overflow;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsMobileMenuOpen(false);
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isMobileMenuOpen]);

  const handleChooseMarket = (country: Country) => {
    window.localStorage.setItem(MARKET_STORAGE_KEY, country.code);
    document.cookie = `${MARKET_COOKIE}=${country.code};max-age=${MARKET_MAX_AGE};path=/;SameSite=Lax`;
    setMarket(country);
  };

  const handleChangeMarket = () => {
    window.localStorage.removeItem(MARKET_STORAGE_KEY);
    document.cookie = `${MARKET_COOKIE}=;max-age=0;path=/;SameSite=Lax`;
    setMarket(null);
  };

  if (!market) {
    return <CountryGate onComplete={handleChooseMarket} />;
  }

  if (showCartView) {
    return <CartView onGoBack={() => setShowCartView(false)} />;
  }

  if (infoPage) {
    return <InfoView page={infoPage} onGoBack={() => setInfoPage(null)} />;
  }

  const currentUserFullName = getFullName(currentUser?.firstName, currentUser?.lastName);
  const currentUserInitials = getNameInitials(currentUser?.firstName, currentUser?.lastName);
  const difficulties = ['all', 'Principiante', 'Intermedio', 'Avanzado'];
  const activeFilterCount = [selectedCategory, selectedDifficulty, selectedMonth].filter((filter) => filter !== 'all').length;
  const itemsPerPage = 16;
  const totalPages = Math.max(1, Math.ceil(filteredPacks.length / itemsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * itemsPerPage;
  const paginatedPacks = filteredPacks.slice(startIndex, startIndex + itemsPerPage);
  const paginationItems = getPaginationItems(safeCurrentPage, totalPages);

  const resetFilters = () => {
    setSelectedCategory('all');
    setSelectedDifficulty('all');
    setSelectedMonth('all');
  };

  const handleEventLikeChange = (eventId: string, likes: string[]) => {
    const updateEventLikes = (event: ClassData) => (String(event._id || event.title) === String(eventId) ? { ...event, likes } : event);
    setAllEvents((events) => events.map(updateEventLikes));
    setSelectedPreviewEvent((event) => event ? updateEventLikes(event) : null);
  };

  const handleViewDetails = (packId: string) => {
    const event = allEvents.find((item) => item._id === packId || item.title === packId);
    if (event) {
      setSelectedPreviewEvent(event);
      return;
    }
    onViewPackDetails?.(packId);
  };

  const buildPreviewCartItem = (event: ClassData, ticketTier?: EventTicketTier): CartItem => {
    const discount = ticketTier ? 0 : (typeof event.discount === 'number' ? event.discount : 0);
    const basePrice = ticketTier ? ticketTier.price : event.price;
    const price = discount ? basePrice * (1 - discount / 100) : basePrice;

    return {
      id: ticketTier ? `${event._id || event.title}:${ticketTier.id}` : (event._id || event.title),
      title: getPeekPassTitle(event),
      description: getPeekPassSummary(event),
      price,
      quantity: 1,
      originalPrice: basePrice,
      discountPercent: discount,
      currency: event.currency || 'ARS',
      category: mapCategoryToEvent(event.category),
      difficulty: mapDifficultyToExperience(event.difficulty),
      duration: typeof event.duration === 'string' ? parseInt(event.duration, 10) : event.duration,
      instructor: getPeekPassOrganizer(event.organizer?.name),
      image: event.thumbnail,
      videoUrl: event.reelUrl,
      benefits: event.benefits || [],
      requirements: event.requirements || [],
      exercises: event.exercises || [],
      eventDate: getPeekPassDate(event),
      venue: getPeekPassVenue(event),
      ticketTierName: ticketTier?.name,
      eventId: event._id,
      ticketTierId: ticketTier?.id,
      serviceFeeRate: event.serviceFeeRate || 5,
      organizerId: event.organizer?.id,
      courseId: event._id,
      canReceivePayments: event.organizer?.canReceivePayments
    };
  };

  const addPreviewItemToCart = (item: CartItem) => {
    const result = addToCart(item);
    if (result.success) return true;

    if (window.confirm(`${result.error}\n\n¿Querés vaciar el carrito y continuar con este evento?`)) {
      replaceCartItems([item]);
      return true;
    }

    return false;
  };

  const handleReservePreview = (event: ClassData, ticketTier?: EventTicketTier) => {
    const item = buildPreviewCartItem(event, ticketTier);
    if (!addPreviewItemToCart(item)) return;
    setSelectedPreviewEvent(null);
    onViewPackDetails?.('cart');
  };

  const handleCheckoutPreview = async (event: ClassData, ticketTier?: EventTicketTier) => {
    if (!event._id || event.isFallback || event._id.startsWith('mock-')) {
      throw new Error('El pago directo está disponible cuando el organizador publica el evento.');
    }

    const item = buildPreviewCartItem(event, ticketTier);

    if (!getCurrentUser() || !getCurrentToken()) {
      if (!addPreviewItemToCart(item)) {
        throw new Error('La compra no se inició porque el carrito actual pertenece a otro organizador.');
      }

      setSelectedPreviewEvent(null);
      redirectToCheckoutAuth({
        type: 'direct-checkout',
        returnTo: '/cart?checkout=1',
        eventId: event._id,
        ticketTierId: ticketTier?.id
      });
      return;
    }

    try {
      const result = await createMercadoPagoPreference([item], {
        externalReferencePrefix: `peekpass_event_${event._id}`,
        organizerId: item.organizerId || null,
        courseId: event._id
      });
      const paymentUrl = result.payment_url || result.init_point;
      if (!paymentUrl) throw new Error('Mercado Pago no devolvió una URL de pago.');
      window.location.href = paymentUrl;
    } catch (error: any) {
      if (error instanceof CheckoutAuthRequiredError) {
        if (!addPreviewItemToCart(item)) return;
        setSelectedPreviewEvent(null);
        redirectToCheckoutAuth({
          type: 'direct-checkout',
          returnTo: '/cart?checkout=1',
          eventId: event._id,
          ticketTierId: ticketTier?.id
        });
        return;
      }

      if (error instanceof CheckoutRevalidationError) {
        const updatedItems = applyCheckoutUpdatesToCart([item], error.result);
        replaceCartItems(updatedItems);
        const changes = summarizeCheckoutChanges(error.result);

        if (updatedItems.length && window.confirm(`${changes}\n\nActualizamos tu carrito. ¿Querés confirmar los cambios y continuar con Mercado Pago?`)) {
          const result = await createMercadoPagoPreference(updatedItems, {
            acceptChanges: true,
            externalReferencePrefix: `peekpass_event_${event._id}`,
            organizerId: updatedItems[0]?.organizerId || null,
            courseId: event._id
          });
          const paymentUrl = result.payment_url || result.init_point;
          if (!paymentUrl) throw new Error('Mercado Pago no devolvió una URL de pago.');
          window.location.href = paymentUrl;
          return;
        }

        throw new Error(updatedItems.length ? 'Revisá el carrito actualizado antes de continuar.' : changes);
      }

      throw error;
    }
  };

  const handleGoToDashboard = () => {
    if (onGoToDashboard) {
      onGoToDashboard();
      return;
    }
    window.location.href = '/dashboard';
  };

  const handleLogout = () => {
    if (!window.confirm('¿Seguro que querés cerrar sesión?')) return;

    clearUserSession();
    localStorage.removeItem('peekpass-session');
    localStorage.removeItem('peekpass-token');
    localStorage.removeItem('peekpass-refresh');
    document.cookie = 'peekpass-session=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
    document.cookie = 'peekpass-token=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
    document.cookie = 'peekpass-refresh=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
    setCurrentUser(null);
    window.location.reload();
  };

  const runMobileMenuAction = (action: () => void) => {
    setIsMobileMenuOpen(false);
    window.setTimeout(action, 0);
  };

  return (
    <div className="min-h-screen w-full bg-[#f8f5ef] text-slate-950">
      <header className="sticky top-0 z-40 w-full border-b border-white/10 bg-black">
        <div className="flex w-full items-center gap-2 px-3 py-2 sm:gap-3 sm:px-6 lg:px-8">
          <div className="flex h-16 w-[210px] shrink-0 items-center overflow-hidden rounded-xl sm:h-20 sm:w-[316px]">
            <img src="/peekpassclubverde_headerlogo.png" alt={PEEKPASS_BRAND} className="h-full w-full object-cover object-center" />
          </div>

          <label className="hidden min-w-0 flex-1 items-center gap-3 rounded-full border border-[#dff38c]/35 bg-white/5 px-5 py-3 transition focus-within:border-[#dff38c] focus-within:bg-white/10 min-[769px]:flex lg:ml-2 xl:max-w-xl 2xl:max-w-2xl">
            <Search className="h-4 w-4 shrink-0 text-[#dff38c]" aria-hidden="true" />
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Buscá artistas o eventos"
              aria-label="Buscar eventos"
              className="min-w-0 flex-1 bg-transparent text-sm font-medium text-[#dff38c] outline-none placeholder:text-[#c7ddb5]"
            />
          </label>

          <nav className="ml-auto hidden items-center rounded-full border border-white/10 bg-white/5 p-1 text-sm font-semibold text-white xl:flex">
            <button type="button" onClick={() => { window.location.href = '/tendencias'; }} className="rounded-full px-3 py-2 transition hover:bg-white/10 hover:text-[#dff38c]">Tendencias</button>
            <button type="button" onClick={() => document.getElementById('agenda')?.scrollIntoView({ behavior: 'smooth' })} className="rounded-full px-3 py-2 transition hover:bg-white/10 hover:text-[#dff38c]">Eventos</button>
            <button type="button" onClick={() => setInfoPage('policies')} className="rounded-full px-3 py-2 transition hover:bg-white/10 hover:text-[#dff38c]">Políticas de uso</button>
          </nav>

          <div className="ml-auto flex items-center gap-2 xl:ml-2">
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(true)}
              className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-white/15 bg-white/5 text-white transition hover:border-[#dff38c] hover:bg-white/10 min-[769px]:hidden"
              aria-label="Abrir menú de navegación"
              aria-expanded={isMobileMenuOpen}
            >
              <Menu className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={handleChangeMarket}
              className="hidden h-11 items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 text-sm font-semibold text-white transition hover:bg-white/10 xl:inline-flex"
              aria-label="Cambiar país"
            >
              <MapPin className="h-4 w-4" />
              {market.name}
            </button>
            <button
              type="button"
              onClick={() => setShowCartView(true)}
              className="hidden h-11 items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 text-sm font-semibold text-white transition hover:bg-white/10 min-[769px]:inline-flex sm:px-4"
            >
              <Ticket className="h-4 w-4" />
              <span className="hidden sm:inline">{cartState.itemCount > 0 ? `${cartState.itemCount} reservas` : 'Reservas'}</span>
            </button>

            {currentUser ? (
              <>
                <button
                  type="button"
                  onClick={handleGoToDashboard}
                  className="hidden h-11 items-center gap-2 rounded-full bg-white px-4 text-sm font-semibold text-slate-950 min-[769px]:inline-flex"
                >
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-black text-xs font-bold text-white">{currentUserInitials}</span>
                  {currentUserFullName || userEmail || 'Mi panel'}
                </button>
                <button type="button" onClick={handleLogout} className="hidden h-11 items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 text-sm font-semibold text-white transition hover:bg-white/10 min-[769px]:inline-flex">
                  <LogOut className="h-4 w-4" />
                  Salir
                </button>
              </>
            ) : (
              <>
                <button type="button" onClick={onLogin} className="hidden h-11 rounded-full border border-white/10 bg-white/5 px-4 text-sm font-semibold text-white min-[769px]:inline-flex min-[769px]:items-center">Ingresar</button>
                <button type="button" onClick={onRegister} className="hidden h-11 items-center rounded-full bg-white px-4 text-sm font-semibold text-slate-950 min-[769px]:inline-flex">Crear cuenta</button>
              </>
            )}
          </div>
        </div>
        {isMobileMenuOpen ? (
          <div className="fixed inset-0 z-[120] min-[769px]:hidden" role="dialog" aria-modal="true" aria-label="Menú móvil de PEEK PASS">
            <button
              type="button"
              aria-label="Cerrar menú"
              onClick={() => setIsMobileMenuOpen(false)}
              className="absolute inset-0 bg-black/55 backdrop-blur-[2px]"
            />
            <aside className="absolute right-0 top-0 flex h-full w-[min(88vw,360px)] flex-col overflow-y-auto border-l border-black/10 bg-[#f8f5ef] p-5 text-slate-950 shadow-[0_30px_90px_-30px_rgba(0,0,0,0.65)]">
              <div className="flex items-center justify-between gap-3 border-b border-slate-200 pb-4">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[#536d12]">PEEK PASS CLUB</p>
                  <h2 className="mt-1 text-2xl font-black tracking-[-0.06em]">Menú</h2>
                </div>
                <button
                  type="button"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-950"
                  aria-label="Cerrar menú"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mt-5 rounded-3xl border border-slate-200 bg-white p-3">
                <p className="px-2 pb-2 text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Navegación</p>
                <div className="grid gap-2">
                  <button type="button" onClick={() => runMobileMenuAction(() => { window.location.href = '/tendencias'; })} className="rounded-2xl px-4 py-3 text-left text-sm font-black transition hover:bg-[#f1facd]">Tendencias y novedades</button>
                  <button type="button" onClick={() => runMobileMenuAction(() => document.getElementById('agenda')?.scrollIntoView({ behavior: 'smooth' }))} className="rounded-2xl px-4 py-3 text-left text-sm font-black transition hover:bg-[#f1facd]">Eventos disponibles</button>
                  <button type="button" onClick={() => runMobileMenuAction(() => setShowCartView(true))} className="flex items-center justify-between rounded-2xl px-4 py-3 text-left text-sm font-black transition hover:bg-[#f1facd]">
                    <span>Mis reservas</span>
                    <span className="rounded-full bg-black px-2.5 py-1 text-xs text-white">{cartState.itemCount}</span>
                  </button>
                  <button type="button" onClick={() => runMobileMenuAction(handleChangeMarket)} className="rounded-2xl px-4 py-3 text-left text-sm font-black transition hover:bg-[#f1facd]">Cambiar país · {market.name}</button>
                </div>
              </div>

              <div className="mt-4 rounded-3xl border border-slate-200 bg-white p-3">
                <p className="px-2 pb-2 text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Cuenta</p>
                {currentUser ? (
                  <div className="grid gap-2">
                    <button type="button" onClick={() => runMobileMenuAction(handleGoToDashboard)} className="flex items-center gap-3 rounded-2xl bg-black px-4 py-3 text-left text-sm font-black text-white">
                      <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-[#dff38c] text-xs text-slate-950">{currentUserInitials}</span>
                      <span>{currentUserFullName || userEmail || 'Mi panel'}</span>
                    </button>
                    <button type="button" onClick={() => runMobileMenuAction(handleLogout)} className="rounded-2xl px-4 py-3 text-left text-sm font-black text-rose-700 transition hover:bg-rose-50">Salir</button>
                  </div>
                ) : (
                  <div className="grid gap-2">
                    <button type="button" onClick={() => runMobileMenuAction(onLogin)} className="rounded-2xl border border-slate-200 px-4 py-3 text-left text-sm font-black transition hover:border-[#dff38c]">Ingresar</button>
                    <button type="button" onClick={() => runMobileMenuAction(onRegister)} className="rounded-2xl bg-black px-4 py-3 text-left text-sm font-black text-white">Crear cuenta</button>
                  </div>
                )}
              </div>

              <div className="mt-4 rounded-3xl border border-slate-200 bg-white p-3">
                <p className="px-2 pb-2 text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">PEEK PASS</p>
                <div className="grid gap-2">
                  <button type="button" onClick={() => runMobileMenuAction(() => setInfoPage('how-it-works'))} className="rounded-2xl px-4 py-3 text-left text-sm font-bold transition hover:bg-[#f1facd]">Cómo funciona</button>
                  <button type="button" onClick={() => runMobileMenuAction(() => setInfoPage('club'))} className="rounded-2xl px-4 py-3 text-left text-sm font-bold transition hover:bg-[#f1facd]">PEEK PASS CLUB</button>
                  <button type="button" onClick={() => runMobileMenuAction(() => setInfoPage('support'))} className="rounded-2xl px-4 py-3 text-left text-sm font-bold transition hover:bg-[#f1facd]">Soporte y contacto</button>
                  <button type="button" onClick={() => runMobileMenuAction(() => setInfoPage('policies'))} className="rounded-2xl px-4 py-3 text-left text-sm font-bold transition hover:bg-[#f1facd]">Políticas de uso</button>
                  <button type="button" onClick={() => runMobileMenuAction(() => setInfoPage('privacy'))} className="rounded-2xl px-4 py-3 text-left text-sm font-bold transition hover:bg-[#f1facd]">Privacidad</button>
                  <button type="button" onClick={() => runMobileMenuAction(() => setInfoPage('terms'))} className="rounded-2xl px-4 py-3 text-left text-sm font-bold transition hover:bg-[#f1facd]">Términos y condiciones</button>
                </div>
              </div>
            </aside>
          </div>
        ) : null}
      </header>

      <main className="w-full max-w-full overflow-x-hidden">
        <section className="w-full px-4 pt-5 sm:px-6 lg:px-8 lg:pt-7">
          <div className="peekpass-hero-shell relative min-h-[340px] overflow-hidden rounded-[1.8rem] bg-black shadow-[0_28px_70px_-40px_rgba(15,23,42,0.65)] sm:min-h-[420px] lg:min-h-[500px]">
            <div className="absolute inset-0 bg-slate-950" aria-hidden="true">
              {heroImages.map((image, index) => (
                <img
                  key={image}
                  src={image}
                  alt=""
                  className={`absolute inset-0 h-full w-full object-cover object-center transition-[opacity,transform,filter] duration-[1200ms] ease-out ${index === activeHeroImage ? 'scale-100 opacity-100' : 'scale-[1.035] opacity-0'}`}
                />
              ))}
            </div>
            <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.88)_0%,rgba(0,0,0,0.68)_42%,rgba(0,0,0,0.22)_100%)]" />
            <div className="peekpass-hero-dots absolute inset-0 opacity-60" aria-hidden="true" />
            <div className="relative flex min-h-[340px] max-w-3xl flex-col justify-end p-6 text-white sm:min-h-[420px] sm:p-10 lg:min-h-[500px] lg:p-14">
              <p className="text-xs font-bold uppercase tracking-[0.26em] text-[#dff38c]">PEEK PASS · {market.name}</p>
              <h1 className="mt-4 text-4xl font-black leading-[0.98] tracking-[-0.07em] sm:text-5xl lg:text-6xl">Tu próxima fecha empieza acá.</h1>
              <p className="mt-4 max-w-xl text-sm leading-6 text-white/75 sm:text-base">Conciertos, festivales, teatro y experiencias para vivir en primera fila.</p>
              <button type="button" onClick={() => document.getElementById('agenda')?.scrollIntoView({ behavior: 'smooth' })} className="peekpass-green-button mt-7 inline-flex w-fit items-center gap-2 rounded-xl px-5 py-3 text-sm font-black text-[#efffb4] transition duration-300 hover:-translate-y-0.5">Explorar eventos <ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        </section>

        <section id="novedades" className="w-full px-4 pb-8 pt-8 sm:px-6 lg:px-8 lg:pt-10">
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">· {PEEKPASS_DOMAIN} ·</p>
              <h2 className="mt-2 text-3xl font-black tracking-[-0.055em] text-slate-950 sm:text-4xl">Tendencias para vivir en vivo</h2>
              <p className="mt-2 text-base text-slate-600">Artistas, fechas y experiencias que están moviendo la agenda.</p>
            </div>
            <button type="button" onClick={() => document.getElementById('agenda')?.scrollIntoView({ behavior: 'smooth' })} className="inline-flex w-fit rounded-full bg-black px-5 py-3 text-sm font-bold text-white">
              Ver todos los eventos
            </button>
          </div>

          <div
            ref={carouselRef}
            className="peekpass-carousel flex gap-4 overflow-x-auto px-2 py-5 min-[769px]:overflow-hidden"
            aria-label="Tendencias y eventos destacados en movimiento continuo."
          >
            {carouselEvents.map((event, index) => (
              <button
                key={`${event._id}-${index}`}
                type="button"
                onClick={() => handleViewDetails(event._id || event.title)}
                className="group relative z-0 min-w-[230px] shrink-0 overflow-hidden rounded-[1.35rem] border border-slate-200 bg-white text-left shadow-[0_16px_36px_-28px_rgba(15,23,42,0.32)] transition duration-300 hover:z-10 hover:scale-[1.03] hover:border-[#b9d950] hover:bg-[#f7fce6] hover:shadow-[0_28px_56px_-28px_rgba(83,108,21,0.42)] focus:z-10 focus:scale-[1.03] focus:border-[#b9d950] sm:min-w-[280px]"
              >
                <div className="relative h-40 overflow-hidden sm:h-44">
                  <img src={getFullImageUrl(event.thumbnail)} alt={event.title} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                  <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/55 to-transparent" />
                  <span className="absolute left-4 top-4 rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-950">{mapCategoryToEvent(event.category)}</span>
                  <span className={`absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold shadow-sm ${event.likes?.length ? 'border-rose-200 bg-rose-50 text-rose-600' : 'border-white/20 bg-black/65 text-white'}`}><Heart className={`h-3.5 w-3.5 ${event.likes?.length ? 'fill-current' : ''}`} />{event.likes?.length || 0}</span>
                  <span className="absolute bottom-4 left-4 text-sm font-semibold text-white">{getPeekPassDate(event)}</span>
                </div>
                <div className="p-4">
                  <p className="text-base font-bold tracking-[-0.04em] text-slate-950 sm:text-lg">{getPeekPassTitle(event)}</p>
                  <p className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-600 sm:text-sm"><MapPin className="h-3.5 w-3.5 shrink-0" />{getPeekPassVenue(event)}</p>
                  <div className="mt-3 flex items-center justify-between gap-3 rounded-xl transition group-hover:bg-[#eaf7b8] group-hover:px-2 group-hover:py-1">
                    <span className="text-xs font-semibold text-slate-500 sm:text-sm">{mapDifficultyToExperience(event.difficulty)}</span>
                    <span className="text-xs font-black text-slate-950 sm:text-sm">${event.price.toLocaleString('es-AR')}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {!isLoadingEvents && eventsError ? (
            <div className="mt-4 rounded-2xl border border-[#31472f] bg-[#101b12] px-5 py-4 text-sm text-[#d9e8d5]">
              {eventsError}
            </div>
          ) : null}
        </section>

        <section id="agenda" className="peekpass-agenda-surface w-full border-t border-[#203120] px-4 py-10 sm:px-6 lg:px-8">
          <div className="peekpass-agenda-content">
          <div className="relative mx-auto mb-7 flex max-w-7xl justify-end">
            <button
              type="button"
              onClick={() => setIsFilterMenuOpen((isOpen) => !isOpen)}
              aria-expanded={isFilterMenuOpen}
              aria-controls="event-filters"
              className={`inline-flex h-12 items-center gap-3 rounded-full border px-5 text-sm font-black transition ${isFilterMenuOpen ? 'border-[#dff38c] bg-[#dff38c] text-slate-950' : 'border-white/20 bg-black/20 text-white hover:border-[#dff38c]/60 hover:bg-white/10'}`}
            >
              <Filter className="h-4 w-4" />
              Filtrar
              {activeFilterCount ? <span className="grid h-5 min-w-5 place-items-center rounded-full bg-black px-1 text-[10px] text-[#dff38c]">{activeFilterCount}</span> : null}
              {isFilterMenuOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>

            {isFilterMenuOpen ? (
              <div id="event-filters" className="absolute right-0 top-full z-20 mt-3 w-full max-w-[620px] rounded-[1.6rem] border border-white/15 bg-[#102116] p-5 text-white shadow-[0_28px_70px_-34px_rgba(0,0,0,0.8)] backdrop-blur sm:p-6">
                <div className="flex items-start justify-between gap-5">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.22em] text-[#dff38c]">Encontrá tu próxima fecha</p>
                    <p className="mt-1 text-sm text-white/65">Los resultados se actualizan al elegir cada opción.</p>
                  </div>
                  {activeFilterCount ? <button type="button" onClick={resetFilters} className="shrink-0 text-xs font-bold text-[#dff38c] transition hover:text-white">Limpiar</button> : null}
                </div>

                <div className="mt-6">
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-white/45">Categoría</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {['all', ...categoryOptions].map((category) => (
                      <button key={category} type="button" onClick={() => setSelectedCategory(category)} className={`rounded-full px-3 py-2 text-xs font-bold transition ${selectedCategory === category ? 'bg-[#dff38c] text-slate-950' : 'border border-white/15 bg-white/5 text-white/75 hover:border-[#dff38c]/50 hover:text-white'}`}>{category === 'all' ? 'Todas' : category}</button>
                    ))}
                  </div>
                </div>

                <div className="mt-6 grid gap-6 sm:grid-cols-2">
                  <div>
                    <p className="text-[11px] font-black uppercase tracking-[0.18em] text-white/45">Tipo de acceso</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {difficulties.map((difficulty) => (
                        <button key={difficulty} type="button" onClick={() => setSelectedDifficulty(difficulty)} className={`rounded-full px-3 py-2 text-xs font-bold transition ${selectedDifficulty === difficulty ? 'bg-[#dff38c] text-slate-950' : 'border border-white/15 bg-white/5 text-white/75 hover:border-[#dff38c]/50 hover:text-white'}`}>{difficulty === 'all' ? 'Todos' : difficulty === 'Principiante' ? 'General' : difficulty === 'Intermedio' ? 'Preferencial' : 'Premium'}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-[11px] font-black uppercase tracking-[0.18em] text-white/45">Mes</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {monthOptions.map((month) => (
                        <button key={month} type="button" onClick={() => setSelectedMonth(month)} className={`rounded-full px-3 py-2 text-xs font-bold capitalize transition ${selectedMonth === month ? 'bg-[#dff38c] text-slate-950' : 'border border-white/15 bg-white/5 text-white/75 hover:border-[#dff38c]/50 hover:text-white'}`}>{month === 'all' ? 'Todos' : month}</button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {isLoadingEvents ? (
            <div className="mx-auto mt-7 max-w-7xl rounded-[1.7rem] border border-white/10 bg-black/20 px-6 py-12 text-center text-sm font-semibold text-[#d9e8d5]">
              Cargando eventos recientes...
            </div>
          ) : paginatedPacks.length ? (
            <div className="mx-auto mt-7 grid max-w-7xl grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {paginatedPacks.map((pack) => (
                <OrganizerCard key={pack._id || pack.title} pack={pack} onViewDetails={handleViewDetails} />
              ))}
            </div>
          ) : (
            <div className="mx-auto mt-7 max-w-7xl rounded-[1.7rem] border border-dashed border-white/15 bg-black/20 px-6 py-12 text-center text-sm text-[#d9e8d5]">
              Todavía no hay eventos publicados para este filtro.
            </div>
          )}

          {totalPages > 1 ? (
            <div className="mx-auto mt-8 flex w-fit max-w-full flex-wrap items-center justify-center gap-x-1 gap-y-3 rounded-[1.35rem] bg-white px-4 py-3 shadow-[0_20px_45px_-28px_rgba(0,0,0,0.6)] sm:flex-nowrap sm:px-5">
              <div className="flex items-center justify-center gap-1">
                <button type="button" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={safeCurrentPage === 1} aria-label="Página anterior" className="inline-flex h-10 min-w-10 items-center justify-center rounded-lg text-slate-500 transition hover:bg-[#eef8ca] hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
                {paginationItems.map((item) => item === 'start-gap' || item === 'end-gap' ? (
                  <span key={item} className="inline-flex h-10 w-7 items-center justify-center text-sm font-bold text-slate-400">…</span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setCurrentPage(item)}
                    aria-current={item === safeCurrentPage ? 'page' : undefined}
                    aria-label={`Ir a la página ${item}`}
                    className={`inline-flex h-10 min-w-10 items-center justify-center rounded-lg px-2 text-sm font-bold transition ${item === safeCurrentPage ? 'bg-[#dff38c] text-slate-950 shadow-[0_10px_20px_-12px_rgba(83,108,18,0.7)]' : 'text-slate-600 hover:bg-[#eef8ca] hover:text-slate-950'}`}
                  >
                    {item}
                  </button>
                ))}
                <button type="button" onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))} disabled={safeCurrentPage === totalPages} aria-label="Página siguiente" className="inline-flex h-10 min-w-10 items-center justify-center rounded-lg text-slate-500 transition hover:bg-[#eef8ca] hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
              </div>
              <span className="w-full text-center text-xs font-bold uppercase tracking-[0.16em] text-[#536d12] sm:ml-3 sm:w-auto">Página {safeCurrentPage} de {totalPages}</span>
            </div>
          ) : null}
          </div>
        </section>

        <footer className="w-full bg-[#111827] px-4 py-12 text-white sm:px-6 lg:px-8">
          <div className="grid gap-9 lg:grid-cols-[1.35fr_1fr_1fr_1fr]">
            <div>
              <div className="flex items-center gap-4">
                <img src="/pkletras_logofooter.jpg" alt={PEEKPASS_BRAND} className="h-16 w-16 rounded-2xl object-cover" />
                <div>
                  <p className="text-xl font-black tracking-[-0.04em]">PEEK PASS.</p>
                  <p className="mt-1 text-xs font-bold uppercase tracking-[0.2em] text-[#dff38c]">PEEK PASS CLUB</p>
                </div>
              </div>
              <p className="mt-5 max-w-sm text-sm leading-7 text-white/70">Entradas para conciertos, teatro, festivales, deportes y experiencias en vivo. Una agenda para descubrir qué sigue.</p>
              <p className="mt-4 flex flex-wrap items-center gap-2 text-sm font-semibold text-white"><span>Redes:</span><a href="https://x.com/" target="_blank" rel="noreferrer" className="transition hover:text-[#dff38c] hover:underline underline-offset-4" aria-label="Abrir X en una nueva pestaña">X</a><span className="text-white/40">·</span><a href="https://www.tiktok.com/" target="_blank" rel="noreferrer" className="transition hover:text-[#dff38c] hover:underline underline-offset-4" aria-label="Abrir TikTok en una nueva pestaña">TikTok</a><span className="text-white/40">·</span><a href="https://www.instagram.com/" target="_blank" rel="noreferrer" className="transition hover:text-[#dff38c] hover:underline underline-offset-4" aria-label="Abrir Instagram en una nueva pestaña">Instagram</a></p>
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-white/50">Información</p>
              <div className="mt-4 flex flex-col items-start gap-3 text-sm text-white/80">
                <button type="button" onClick={() => setInfoPage('how-it-works')} className="text-left transition hover:text-[#dff38c]">Cómo funciona</button>
                <button type="button" onClick={() => setInfoPage('policies')} className="text-left transition hover:text-[#dff38c]">Políticas de uso</button>
                <button type="button" onClick={() => setInfoPage('privacy')} className="text-left transition hover:text-[#dff38c]">Privacidad</button>
                <button type="button" onClick={() => setInfoPage('terms')} className="text-left transition hover:text-[#dff38c]">Términos y condiciones</button>
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-white/50">Club</p>
              <p className="mt-4 text-sm leading-7 text-white/75">Puntos, preventas y beneficios pensados para quienes viven más fechas.</p>
              <button type="button" onClick={() => setInfoPage('club')} className="mt-4 rounded-full border border-white/20 px-4 py-2 text-sm font-bold text-white transition hover:border-[#dff38c] hover:text-[#dff38c]">Conocé PEEK PASS CLUB</button>
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-white/50">Soporte y asistencia</p>
              <p className="mt-4 text-sm leading-7 text-white/75">Asistencia para compras, reservas y publicaciones de eventos.</p>
              <button type="button" onClick={() => setInfoPage('support')} className="mt-4 text-sm font-bold text-[#dff38c] underline underline-offset-4">Soporte y contacto</button>
              <a href="mailto:peekpassclub@gmail.com" className="mt-3 block text-sm text-white/80 transition hover:text-[#dff38c]">peekpassclub@gmail.com</a>
            </div>
          </div>
        </footer>
      </main>
      {selectedPreviewEvent ? <EventPreviewModal event={selectedPreviewEvent} onClose={() => setSelectedPreviewEvent(null)} onAddToCart={handleReservePreview} onCheckout={handleCheckoutPreview} onRequireLogin={onRegister} onLikeChange={handleEventLikeChange} /> : null}
    </div>
  );
};

export default Home;
